import { ValidationError } from '.';

/**
 * Updates multiple form fields and clears associated validation errors
 * @param setForm - React setState function to update form state
 * @param setValidationErrors - React setState function to update validation errors
 * @returns Function that takes field paths and update function to modify form state
 * @example
 * const updateMultiple = updateFormMulti(setForm, setValidationErrors);
 * updateMultiple([['contact', 'name'], ['contact', 'email']], (prev) => ({
 *   ...prev,
 *   contact: { ...prev.contact, name: 'John', email: 'john@example.com' }
 * }));
 */
export const updateFormMulti =
  <T,>(
    setForm: React.Dispatch<React.SetStateAction<T>>,
    setValidationErrors: React.Dispatch<React.SetStateAction<ValidationError[]>>
  ) =>
  (fieldPartsList: string[][], update: (prev: T) => T) => {
    fieldPartsList.forEach((fieldParts) =>
      setValidationErrors((prev) =>
        prev.filter(
          (e) =>
            e.field.length !== fieldParts.length || !e.field.every((v, i) => v === fieldParts[i])
        )
      )
    );
    setForm((prev) => update(prev));
  };

/**
 * Updates a single form field and clears associated validation errors
 * @param setForm - React setState function to update form state
 * @param setValidationErrors - React setState function to update validation errors
 * @returns Function that takes field path and update function to modify form state
 * @example
 * const update = updateForm(setForm, setValidationErrors);
 * update(['contact', 'name'], (prev) => ({
 *   ...prev,
 *   contact: { ...prev.contact, name: 'John' }
 * }));
 */
export const updateForm =
  <T,>(
    setForm: React.Dispatch<React.SetStateAction<T>>,
    setValidationErrors: React.Dispatch<React.SetStateAction<ValidationError[]>>
  ) =>
  (fieldParts: string[], update: (prev: T) => T) => {
    setValidationErrors((prev) => prev.filter((e) => !getValidationError([e], fieldParts)));
    setForm((prev) => update(prev));
  };

/**
 * Gets validation error message for a specific field path
 * @param validationErrors - Array of validation errors
 * @param fieldParts - Array of strings representing path to form field
 * @returns Error message if validation error exists for field
 * @example
 * const errorMessage = getError(validationErrors, ['contact', 'email']);
 */
export const getValidationError = (validationErrors: ValidationError[], fieldParts: string[]) =>
  validationErrors.find(
    (e) =>
      e.field.length >= fieldParts.length &&
      e.field.slice(-fieldParts.length).every((v, i) => v === fieldParts[i])
  )?.message;

/**
 * Hook that provides form update and validation error utilities
 * @param setForm - React setState function to update form state
 * @param setValidationErrors - React setState function to update validation errors
 * @returns Object containing form update and error checking functions
 * @example
 * const { updateForm, updateFormMulti, getError } = useFormActions(setForm, setValidationErrors);
 */
export const useFormActions = <T,>(
  setForm: React.Dispatch<React.SetStateAction<T>>,
  setValidationErrors: React.Dispatch<React.SetStateAction<ValidationError[]>>
) => ({
  updateForm: updateForm(setForm, setValidationErrors),
  updateFormMulti: updateFormMulti(setForm, setValidationErrors),
  getValidationError
});
